"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { SplitText } from "gsap/dist/SplitText";
import { cn } from "@/lib/utils";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { type CarouselApi } from "@/shadcn/ui/carousel";

import BlackPill from "@/components/getting-started/BlackPill";
import { CTAButton } from "@/shadcn/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import griBGimg from "@/public/lp2/hero/grid-bg.svg";
import logoOpaque from "@/public/mentoring/logo-opaque.svg";
import arrowRight from "@/public/lp2/component2/right.svg";
import trademaniaLogoImg from "@/public/trading-guide/logo.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);
}

export default function Hero({
  data,
}: {
  data: PageMentoringSchema;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const containerRef2 = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const originalVideoTop = gsap.getProperty("#video-bg", "top");

      gsap.set("#video-bg", { top: 0 });
      gsap.set(containerRef.current, { visibility: "visible" });

      const heroTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.4 },
      });

      heroTL
        .from("#grid-bg", {})
        .from("#video-bg", {
          duration: 0.4,
        })
        .from("#texts-div > *", {
          stagger: 0.1,
          delay: 0.25,
          duration: 0.3,
        })
        .to(
          "#video-bg",
          {
            opacity: 1,
            top: `${originalVideoTop}px`,
            clearProps: "all",
          },
          "<"
        )
        .from(
          "#tabs-div",
          {
            duration: 0.45,
            clearProps: "all",
          },
          "-=0.4"
        );
    },
    { scope: containerRef }
  );

  useGSAP(
    () => {
      if (!containerRef2.current) return;

      const heroTL2 = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      heroTL2.from(containerRef2.current.children, {
        y: "3rem",
        stagger: 0.1,
        delay: 0.25,
        scrollTrigger: {
          trigger: containerRef2.current,
          end: "center center",
          scrub: 1,
        },
      });
    },
    { scope: containerRef2 }
  );

  const changeCurrent = useCallback(
    (index: number) => {
      if (!api) {
        return;
      }

      api.scrollTo(index);
    },
    [api]
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <>
      <section
        ref={containerRef}
        id="hero" // To set hidden initial state with global CSS
        className={cn(
          "w-full relative overflow-hidden bg-[#000C08] mix-blend-lighten",
          "pt-[13rem] lg:pt-[20rem] pb-[12rem] lg:pb-[20rem]"
        )}
      >
        {/* Backgrounds */}
        <Image
          id="grid-bg"
          src={griBGimg}
          alt="Grid background"
          className="absolute z-[-1] top-0 left-0 right-0 h-full w-full object-cover pointer-events-none mix-blend-lighten"
        />

        <HLSVideoPlayer
          id="video-bg"
          controls={false}
          playsInline={true}
          autoPlay={true}
          muted={true}
          loop={true}
          className="absolute z-[-2] top-[28%] lg:top-[25.5rem] left-0 right-0 h-[50%] lg:h-auto w-full !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
          source={{
            embed: false,
            muxVideo: {
              assetId: "",
              filename: "video-bg",
              status: "ready",
              playbackId: "lmI777ubRl3wQMn1UiNAFSW00M5rD4m01n3jyOfO4rprc",
            },
            embedUrl: "",
            thumbnail: {
              src: "",
              width: 0,
              height: 0,
              lqip: "",
              name: "",
            },
          }}
        />
        {/* End Backgrounds */}

        <div className="container-lp mx-auto relative">
          <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
            <div
              id="texts-div"
              className="w-full md:w-4/5 lg:w-[min(108rem,60%)] mb-[4rem] lg:mb-[16rem] flex flex-col items-center justify-center"
            >
              {data?.heroTitles && (
                <PortableText
                  value={data?.heroTitles ?? []}
                  components={customRichTextComponent({
                    block: {
                      h1: ({ children }) => (
                        <h1 className="gradient-title text-[5rem] lg:text-[8rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.56rem] leading-[110%] text-center">
                          {children}
                        </h1>
                      ),
                      normal: ({ children }) => (
                        <p className="text-[2.3rem] lg:text-[2rem] font-[500] leading-[3.375rem] text-[#DADADA] opacity-80 lg:opacity-60 text-center mt-[2rem] mb-[2rem] lg:mb-[3.24rem] max-w-[116rem]">
                          {children}
                        </p>
                      ),
                    },
                  })}
                />
              )}

              {data?.heroCTA && <CTAButton data={data?.heroCTA} />}
            </div>
          </div>
        </div>

        {/* desktop */}
        <div
          id="tabs-div"
          className="hidden lg:grid w-full grid-cols-1 lg:grid-cols-5 justify-center items-center gap-[6rem] lg:gap-[2.5rem] max-w-[225rem] mx-auto"
        >
          {data?.heroCards?.map((card, index) => {
            return (
              <div
                key={index}
                className={`w-full h-full max-w-[42rem] mx-auto lg:mx-0 relative ${
                  index % 2 == 0
                    ? "lg:translate-y-[2rem]"
                    : "lg:translate-y-[-2rem]"
                }`}
              >
                <GroqImage
                  src={card.image}
                  alt="card image"
                  className="!w-full"
                />
                <div className="absolute w-full h-1/3 bottom-0 left-0 right-0 p-[3rem] z-[2]">
                  <h5 className="sans text-white/70 font-[600] text-[2rem] leading-[130%] text-center mb-2">
                    {card.title}
                  </h5>
                  <p className="sans text-white/50 font-[400] text-[2rem] leading-[130%] text-center">
                    {card.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        {/* mobile */}
        <div id="tabs-div" className="lg:hidden w-full">
          <Carousel
            setApi={setApi}
            opts={{
              startIndex: 1,
            }}
          >
            <CarouselContent className="w-full py-[4.5rem]">
              {data?.heroCards?.map((card, index) => {
                return (
                  <CarouselItem key={index} className="basis-[70%]">
                    <div
                      key={index}
                      className={`w-full h-full max-w-[42rem] mx-auto lg:mx-0 relative ${
                        index % 2 == 0
                          ? "translate-y-[2rem]"
                          : "translate-y-[-2rem]"
                      }`}
                    >
                      <Image
                        src={card.image}
                        alt="card image"
                        className="!w-full"
                      />
                      <div className="absolute w-full h-1/3 bottom-0 left-0 right-0 p-[2rem] z-[2]">
                        <h5 className="sans text-white/70 font-[600] text-[2rem] leading-[130%] text-center mb-2">
                          {card.title}
                        </h5>
                        <p className="sans text-white/50 font-[400] text-[2rem] leading-[130%] text-center">
                          {card.text}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
          {/* constrols */}
          <div className="w-full flex flex-col items-center justify-center gap-[8rem] px-4">
            <div className="w-full flex items-center justify-between gap-[4rem]">
              <button
                onClick={() =>
                  changeCurrent(
                    current === 0 ? data?.heroCards.length - 1 : current - 1
                  )
                }
                className="mentoring-slider-indicator-gradient border border-[#bcc6ec80] min-w-[7rem] max-w-[7rem] min-h-[7rem] max-h-[7rem] rounded-full flex items-center justify-center"
              >
                <Image
                  src={arrowRight}
                  alt="arrow left"
                  className="w-[3rem] h-[3rem] rotate-180"
                />
              </button>

              <div className="w-full flex items-stretch justify-center gap-[1.6rem]">
                {data?.heroCards?.map((_, index) => {
                  return (
                    <button
                      key={index}
                      onClick={() => changeCurrent(index)}
                      className={`${
                        index === current ? "opacity-100" : "opacity-20"
                      } bg-[#1ECC97] w-full h-[1.5rem] rounded-full`}
                    ></button>
                  );
                })}
              </div>

              <button
                onClick={() =>
                  changeCurrent(
                    data?.heroCards.length === current + 1 ? 0 : current + 1
                  )
                }
                className="mentoring-slider-indicator-gradient border border-[#bcc6ec80] min-w-[7rem] max-w-[7rem] min-h-[7rem] max-h-[7rem] rounded-full flex items-center justify-center"
              >
                <Image
                  src={arrowRight}
                  alt="arrow right"
                  className="w-[3rem] h-[3rem]"
                />
              </button>
            </div>
          </div>
        </div>
        {/* end mobile */}

        <div className="max-w-[90vw] lg:max-w-[180rem] relative z-[2] mx-auto mt-[10rem]">
          {/* border */}
          <div className="absolute z-[-1] w-full h-full bg-black left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 lg:rounded-[10rem] rounded-[4rem]"></div>
          <div className="absolute z-[-2] w-[calc(100%+.25rem)] h-[calc(100%+.25rem)] bg-gradient-to-r from-[#61FFCE]/50 via-white/20 to-[#61FFCE]/20 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 lg:rounded-[10rem] rounded-[4rem]"></div>
          {/* end border */}
          <div className="lg:rounded-[10rem] rounded-[4rem] backdrop-blur-[4rem] bg-black/20 px-[5rem] py-[3rem] flex flex-col lg:flex-row lg:justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <Image
                src={trademaniaLogoImg}
                alt="logo"
                className="!w-[4rem] !h-[4rem] !min-w-[4rem] !min-h-[4rem]"
              />
              <h3 className="gradient-title sans text-[3rem] lg:text-[3.5rem] font-[500] tracking-[-0.29rem] leading-[110%] lg:leading-[129%]">
                Persönlich. Präzise. Profitabel.
              </h3>
            </div>

            <Image
              src={logoOpaque}
              alt="Logo"
              className="absolute z-[-1] top-1/2 right-[25%] -translate-y-1/2"
            />

            {data?.heroCTA && <CTAButton data={data?.heroCTA} />}

            {/* blur */}
            <div className="absolute z-[-1] w-full h-full bg-[#1ECC97] opacity-20 left-1/2 -translate-x-1/2 top-1/2 rounded-[10rem] blur-[10.25rem]"></div>
            {/* end blur */}
          </div>
        </div>

        {/* fade to next section */}
        <div className="absolute z-[0] left-0 right-0 bottom-0 hero-charts-gradient !h-[10%] lg:!h-[20%]"></div>
      </section>

      <section className="w-full">
        <div className="container-lp mx-auto">
          <div
            ref={containerRef2}
            className="flex flex-col justify-between w-full max-w-[96rem] mx-auto lg:pt-[10rem] lg:pb-[4rem]"
          >
            {data?.pathTexts && (
              <PortableText
                value={data?.pathTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill
                        text={children as string}
                        className="uppercase mb-[4rem] w-fit"
                      />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[4.5rem] lg:text-[7rem] font-[500] tracking-[-0.29rem] leading-[110%] w-full mb-[4rem] lg:mb-[6rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white/80 text-[3rem] lg:text-[3.5rem] font-[400] tracking-[-0.3rem] leading-[140%] mb-[4rem] lg:mb-[6rem]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}
          </div>
        </div>
      </section>
    </>
  );
}
